import * as React from 'react';
import Axios, { CancelToken } from 'axios';
import { TenantContext } from "../contexts/ContextWrapper";
import { TenantContextType } from "../interfaces/ContextType";
import { apiUris } from "../helpers/SettingsMisc";
import { GetAdminSettings, GetAvailableUserDataGroups, GetLogo, GetUserDataGroupsList } from "../helpers/GetFromRestApi";
import { GetUse365Groups, GetUseUserDataGroups } from "../helpers/SettingsCompany";
import { FetchGroups } from "../helpers/Groups";
import { GroupInfo } from "../interfaces/GroupInfo";
import { GroupDetails } from "../interfaces/GroupDetails";
import { AuthContext } from "../contexts/AuthContext";
import { UserDataGroup } from '../interfaces/UserDataGroup';
import { UserAdminItem } from '../interfaces/UserAdminItem';

export const useSettingsFetch = () => {
    const [isLoadingSettings, setIsLoadingSettings] = React.useState<boolean>(false)
    const { accessToken } = React.useContext(AuthContext);

    const { tenantContext, setTenantContext } = React.useContext(TenantContext);


    const Fetch365Groups = async (api: string, domain: string, myTenant: boolean, cancelToken?: CancelToken): Promise<[GroupInfo[], GroupDetails[], GroupDetails[]]> => {
        let companyGroups: GroupDetails[] = []

        const [groupIds, allGroups] = await FetchGroups(accessToken!, domain, api, cancelToken)
        if (!myTenant) {
            companyGroups = groupIds.map(item => {
                return {
                    id: item.groupID,
                    displayName: item.groupName,
                    createdDateTime: null,
                } as GroupDetails
            })
        }
        else {
            companyGroups = allGroups.length ?
                groupIds
                    .map((idItem) => {
                        return allGroups
                            .find(item => item.id === idItem.groupID)
                    })
                    .filter((item): item is GroupDetails => Boolean(item))
                    .sort((a, b) => {
                        let fa = a.displayName.toLowerCase(), fb = b.displayName.toLowerCase();
                        if (fa < fb) { return -1; } if (fa > fb) { return 1; } return 0;
                    })
                : []
        }
        return [groupIds, companyGroups, allGroups];
    }

    const FetchUserDataGroups = async (api: string, domain: string, isSettingsAdmin: boolean): Promise<[GroupInfo[], GroupDetails[], UserDataGroup[]]> => {

        let userDataGroups: UserDataGroup[] = []
        let fetchedMyGroups: UserDataGroup[] = []

        try {
            if (isSettingsAdmin) {
                // SettingsAdmin: fetch all groups + user's own groups
                const results = await Promise.allSettled([
                    GetUserDataGroupsList(accessToken!, api, domain),
                    GetAvailableUserDataGroups(accessToken!, api, domain, true),
                ])

                if (results[0].status === 'fulfilled') {
                    userDataGroups = results[0].value || []
                } else {
                    console.warn('GetUserDataGroupsList failed:', results[0].reason)
                }

                if (results[1].status === 'fulfilled') {
                    fetchedMyGroups = results[1].value || []
                } else {
                    const error = results[1].reason
                    if (error?.response?.status !== 404) {
                        console.warn('GetAvailableUserDataGroups failed:', error)
                    }
                }
            } else {
                // Non-settingsAdmin: only fetch available groups (own groups)
                try {
                    fetchedMyGroups = await GetAvailableUserDataGroups(accessToken!, api, domain, true) || []
                } catch (error: any) {
                    if (error?.response?.status !== 404) {
                        console.warn('GetAvailableUserDataGroups failed:', error)
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching user data groups:', error)
        }

        userDataGroups = Array.isArray(userDataGroups) && userDataGroups.length > 0
            ? userDataGroups
            : (Array.isArray(fetchedMyGroups) ? fetchedMyGroups : []);

        const groupIds = userDataGroups.map(item => ({
            groupID: item.groupID,
            groupName: item.groupName,
            isDeleted: false,
        } as GroupInfo))
        const companyGroups = userDataGroups.map(item => ({
            id: item.groupID,
            displayName: item.groupName,
        } as GroupDetails))
        return [groupIds, companyGroups, fetchedMyGroups];
    }

    const RefreshUserDataGroups = async (api: string, domain: string, userData: UserAdminItem) => {
        try {
            const [groupIds, companyGroups, myGroups] = await FetchUserDataGroups(api, domain, !!userData.isSettingsAdmin || !!userData.isAdmin)

            setTenantContext(prev => ({
                ...prev,
                // 365 Groups take precedence as the source of groupIds/companyGroups (mirrors UpdateSettings)
                ...(GetUse365Groups(prev.settings) ? {} : { groupIds: groupIds, companyGroups: companyGroups }),
                myGroups: myGroups,
            }))
        } catch (err) {
            if (!Axios.isCancel(err)) {
                console.error('Error refreshing user data groups:', err)
            }
        }
    }

    const Update365Groups = async (api: string, domain: string, cancelToken?: CancelToken) => {

        setIsLoadingSettings(true);

        try {
            // Use MyTenant from context - only fetch Graph groups if on user's own tenant
            const [groupIds, companyGroups, allGroups] = await Fetch365Groups(api, domain, tenantContext.MyTenant, cancelToken);

            setTenantContext(prev => ({
                ...prev,
                api: api.toLowerCase(),
                apiUri: apiUris(api),
                groupIds: groupIds,
                companyGroups: companyGroups,
                allGroups: allGroups,
            }));
            setIsLoadingSettings(false);
        } catch (err) {
            if (!Axios.isCancel(err)) {
                console.error('Error updating 365 groups:', err);
                setIsLoadingSettings(false);
            }
        }
    }

    const UpdateSettings = async (api: string, domain: string, userData: UserAdminItem, cancelToken?: CancelToken): Promise<void> => {

        setIsLoadingSettings(true);

        try {
            const [fetchAdminSettings, fetchAdminLogo] = await Promise.all([
                GetAdminSettings(accessToken!, domain, api, cancelToken),
                GetLogo(accessToken!, domain, api, cancelToken)
            ])

            let groupIds: GroupInfo[] = []
            let companyGroups: GroupDetails[] = []
            let allGroups: GroupDetails[] = []
            let myGroups: UserDataGroup[] = []

            const myTenant: boolean = userData.Domains?.find(item => item.DomainName.toLowerCase() === domain.toLowerCase())?.MyTenant ?? false

            if (GetUse365Groups(fetchAdminSettings)) {
                [groupIds, companyGroups, allGroups] = await Fetch365Groups(api, domain, myTenant, cancelToken)
            }
            else if (GetUseUserDataGroups(fetchAdminSettings)) {
                [groupIds, companyGroups, myGroups] = await FetchUserDataGroups(api, domain, !!userData.isSettingsAdmin || !!userData.isAdmin)
            }

            if (fetchAdminSettings || fetchAdminLogo) {
                setTenantContext(prev => ({
                    ...prev,
                    api: api.toLowerCase(),
                    apiUri: apiUris(api),
                    logo: fetchAdminLogo,
                    settings: fetchAdminSettings,
                    tenantName: fetchAdminSettings.Domain?.toLowerCase() ?? '',
                    groupIds: groupIds,
                    companyGroups: companyGroups,
                    allGroups: allGroups,
                    myGroups: myGroups,
                    simulatedGroups: undefined,
                    MyTenant: myTenant,
                }))
            }
            else {
                setTenantContext({} as TenantContextType)
                localStorage.clear()
            }
            setIsLoadingSettings(false);
        } catch (err) {
            if (!Axios.isCancel(err)) {
                console.error('Error updating settings:', err);
                setIsLoadingSettings(false);
            }
        }
    }

    return { UpdateSettings, Update365Groups, RefreshUserDataGroups, isLoadingSettings }
}